import { WatchQueryFetchPolicy } from '@apollo/client';
import { decompressURLSearchParams } from '@modules/common/utils';
import { populatePayloadFromSearchParams } from '@modules/common/utils/populatePayloadFromSearchParams';
import { getCombinedWhereClause } from '@modules/dashboard/helpers';
import { parseStringifiedDuration } from '@modules/feed/utils/parseStringifiedDuration';
import {
  QuantifyDashboardFilters,
  QuantifyQuery,
  QuantifyState,
} from '@modules/quantify/store/types';
import { getQuantifyAggregatePeriodFromDuration } from '@modules/quantify/utils';
import { TimeUnit, useListSourceDetailsQuery } from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useMount, useUpdateEffect } from 'react-use';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import {
  useFetchQuantifyReport,
  useGetQuantifyStateFromAnalysisStore,
  useGetSelectedGroupByOption,
  useGetSelectedMeasuredAsCriteriaOption,
} from '.';
import { QuantifyActionType } from '../store/quantifyActionType';
import { useQuantifyStore } from '../store/quantifyStore';

interface UseUpdateQuantifyStoreFromURLParams {
  fetchPolicy?: WatchQueryFetchPolicy;
  shouldFetchChartData?: boolean;
  shouldFetchTableData?: boolean;
  shouldShowAllTableValues?: boolean;
  shouldShowCoocurringData?: boolean;
  shouldFetchDataAfterSetup?: boolean;
}

export const useUpdateQuantifyStoreFromURL = ({
  fetchPolicy,
  shouldFetchChartData = true,
  shouldFetchTableData = true,
  shouldShowAllTableValues, // don't use default value here, we need to know if this is undefined
  shouldShowCoocurringData,
  shouldFetchDataAfterSetup = true,
}: UseUpdateQuantifyStoreFromURLParams) => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);

  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const [isDataSetupComplete, setIsDataSetupComplete] = useState(false);

  const parsedMetadata = useGetQuantifyStateFromAnalysisStore();
  const fetchQuantifyReport = useFetchQuantifyReport({
    fetchPolicy,
    isInitialFetch: true,
    shouldFetchChartData,
    shouldFetchTableData,
  });

  const { isLoading: isLoadingGroupByOption, selectedGroupByOption } =
    useGetSelectedGroupByOption(parsedMetadata?.query?.groupByField?.field);

  const {
    isLoading: isLoadingMeasuredAsCriteriaOption,
    selectedMeasuredAsCriteriaOption,
  } = useGetSelectedMeasuredAsCriteriaOption({
    formulaID:
      parsedMetadata?.query?.formulaQuery?.eqlFunction ?? EQLFunctionName.Count,
    measuredAsCriteria: parsedMetadata?.query?.formulaQuery?.criteria?.field,
    groupBy: selectedGroupByOption,
  });

  // we fire EQL queries to fetch data which is dependent on source details
  // we need to wait till source details are available before we fetch report
  // data
  const { loading: isLoadingSourceDetails } = useListSourceDetailsQuery({});

  const isLoadingRequiredData =
    isLoadingGroupByOption ||
    isLoadingSourceDetails ||
    isLoadingMeasuredAsCriteriaOption;

  const initQueryPayload = useRef<Partial<QuantifyQuery>>({});

  useEffect(() => {
    if (isLoadingGroupByOption) {
      return;
    }

    if (Object.keys(parsedMetadata?.query ?? {}).length) {
      // Override sortBy if field is label (SortBy label is deprecated)
      const sortBy =
        parsedMetadata?.query?.sortBy?.field === 'label'
          ? null
          : parsedMetadata?.query?.sortBy;

      const shouldShowAllValues =
        shouldShowAllTableValues ?? parsedMetadata?.visual?.shouldShowAllValues;

      const shouldShowCooccurringDataPoints =
        shouldShowCoocurringData ?? parsedMetadata?.query?.isCoOccurring;

      initQueryPayload.current = {
        ...initQueryPayload.current,
        sortBy,
        isCoOccurring: shouldShowCooccurringDataPoints,
        ...parsedMetadata?.query,
      };

      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          ...parsedMetadata?.visual,
          shouldShowAllValues: shouldShowAllValues,
          hasQueryChanged: false,
        },
      });
    }

    const queryPayload: Partial<QuantifyState['query']> = {};
    populatePayloadFromSearchParams(
      queryPayload,
      ['isCoOccurring'],
      decompressURLSearchParams,
    );

    if (queryPayload.isCoOccurring !== undefined) {
      initQueryPayload.current = {
        ...initQueryPayload.current,
        isCoOccurring: queryPayload.isCoOccurring,
      };
    }
  }, [
    dispatch,
    isLoadingGroupByOption,
    parsedMetadata,
    shouldShowAllTableValues,
    shouldShowCoocurringData,
  ]);

  useEffect(() => {
    if (!isLoadingRequiredData && parsedMetadata?.query) {
      const dashboardFiltersPayload: Partial<QuantifyDashboardFilters> = {};

      populatePayloadFromSearchParams(
        dashboardFiltersPayload,
        { duration: 'dashboardDuration', whereClause: 'dashboardWhereClause' },
        decompressURLSearchParams,
      );

      if (dashboardFiltersPayload.duration) {
        dashboardFiltersPayload.duration = parseStringifiedDuration(
          dashboardFiltersPayload.duration,
        );
      }

      if (
        dashboardFiltersPayload.whereClause ||
        dashboardFiltersPayload.duration
      ) {
        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: {
            dashboardFilters: {
              whereClause: dashboardFiltersPayload.whereClause,
              duration: dashboardFiltersPayload.duration,
              aggregatePeriod: dashboardFiltersPayload.duration
                ? getQuantifyAggregatePeriodFromDuration({
                    duration: dashboardFiltersPayload.duration,
                    currentAggregatePeriod:
                      parsedMetadata.query.aggregatePeriod ?? TimeUnit.Month,
                  })
                : undefined,
            },
          },
        });
      }

      const queryPayload: Partial<QuantifyQuery> = {};

      populatePayloadFromSearchParams(
        queryPayload,
        [
          'duration',
          'whereClause',
          'compareQuery',
          'aggregatePeriod',
          'formulaQuery',
          'shouldShowAnomalies',
          'anomalyThreshold',
          'sortBy',
          'compareMetadata',
          'visualisationType',
          'isCoOccurring',
        ],
        decompressURLSearchParams,
      );

      if (queryPayload.duration) {
        queryPayload.duration = parseStringifiedDuration(queryPayload.duration);
      }
      if (queryPayload.whereClause) {
        queryPayload.whereClause = {
          ...getCombinedWhereClause(
            parsedMetadata.query?.whereClause ?? EmptyWhereClause,
            queryPayload.whereClause,
          ),
        };
      }

      if (
        queryPayload.compareQuery &&
        queryPayload.compareQuery.whereClause &&
        queryPayload?.whereClause
      ) {
        queryPayload.formulaQuery = {
          ...queryPayload.formulaQuery,
          denominatorWhereClause: queryPayload.whereClause,
        };
      }

      dispatch({
        type: QuantifyActionType.INIT_QUERY,
        payload: {
          ...initQueryPayload.current,
          ...(queryPayload.duration ||
          queryPayload.whereClause ||
          queryPayload.compareQuery
            ? { ...parsedMetadata.query, ...queryPayload }
            : parsedMetadata.query),
          groupByField:
            selectedGroupByOption ?? parsedMetadata.query.groupByField,
          aggregatePeriod:
            queryPayload.aggregatePeriod ??
            parsedMetadata.query.aggregatePeriod,
          formulaQuery: {
            ...parsedMetadata.query.formulaQuery,
            ...queryPayload.formulaQuery,
            criteria:
              selectedMeasuredAsCriteriaOption ??
              parsedMetadata.query.formulaQuery?.criteria,
          },
        },
      });

      if (
        queryPayload.duration ||
        queryPayload.whereClause ||
        queryPayload.compareQuery
      ) {
        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: {
            hasQueryChanged: true,
          },
        });
      }

      if (parsedMetadata.visual?.isCustomAnomalyThresholdSelected) {
        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: {
            isCustomAnomalyThresholdSelected:
              parsedMetadata.visual.isCustomAnomalyThresholdSelected,
          },
        });
      }

      setShouldFetchReportData(true);
      setIsDataSetupComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isLoadingRequiredData,
    parsedMetadata?.query,
    selectedGroupByOption?.field,
    selectedMeasuredAsCriteriaOption?.field,
  ]);

  useMount(() => {
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        isEditMode: false,
      },
    });
  });

  useUpdateEffect(() => {
    if (shouldFetchReportData && shouldFetchDataAfterSetup) {
      fetchQuantifyReport().then(() => setShouldFetchReportData(false));
    }
  }, [shouldFetchReportData, shouldFetchDataAfterSetup]);

  return {
    isLoading: isLoadingRequiredData,
    isFetchingReportData: shouldFetchReportData && shouldFetchDataAfterSetup,
    isDataSetupComplete,
  };
};
